import { css } from 'aphrodite';
import * as React from 'react';
import { FieldError } from 'react-hook-form';
import { toast } from 'sonner';
import { SurveyControllerType } from '../../../../../queries/Surveys/types';
import { useClientUploadSurveyAttachmentByToken } from '../../../../../queries/Surveys/useClientUploadSurveyAttachmentByToken';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { IPublicFileAttachment } from '../../../../models/Api';
import { ErrorMessage } from '../../Custom/components/CustomSurvey/CustomSurveyResponseForm/ErrorMessage';
import { styleSheet } from './styles';
import { acceptedFileTypes, SURVEY_FILE_INPUT_MAX_FILES } from './utils';

interface FileUploadProps {
	onFileSelected: (files: IPublicFileAttachment[]) => void;
	files: IPublicFileAttachment[];
	token: string;
	surveyType: SurveyControllerType;
	onDeleteFile?: (file: IPublicFileAttachment) => void;
	error?: FieldError;
}

export const FileUpload = ({ onFileSelected, files, token, surveyType, onDeleteFile, error }: FileUploadProps) => {
	const uploadMutation = useClientUploadSurveyAttachmentByToken({
		onError: e => {
			e.failed?.forEach(failedFile => {
				toast.error(failedFile.systemMessage);
			});
		},
	});

	const [isDragging, setIsDragging] = React.useState(false);
	const inputRef = React.useRef<HTMLInputElement>(null);

	const handleFiles = async (newFiles: File[]) => {
		const remainingSlots = SURVEY_FILE_INPUT_MAX_FILES - files.length;
		if (remainingSlots <= 0) {
			toast.error(`Maximum of ${SURVEY_FILE_INPUT_MAX_FILES} files allowed`);
			return;
		}

		// Filter out unsupported file types
		const validFiles = newFiles.filter(file => {
			if (!acceptedFileTypes.includes(file.type)) {
				toast.error(`${file.name} is not a supported file type`);
				return false;
			}
			return true;
		});

		if (validFiles.length === 0) {
			return;
		}

		const filesToAdd = validFiles.slice(0, remainingSlots);
		if (validFiles.length > remainingSlots) {
			toast.warning(`Only ${remainingSlots} file(s) can be added. Maximum is ${SURVEY_FILE_INPUT_MAX_FILES} files.`);
		}

		const results = await Promise.allSettled(
			filesToAdd.map(file => uploadMutation.mutateAsync({ file, accessToken: token, surveyType }))
		);

		const successfulUploads: IPublicFileAttachment[] = [];
		for (const result of results) {
			if (result.status === 'fulfilled') {
				successfulUploads.push(...result.value.map(y => ({ fileName: y.fileName, attachmentId: y.id, id: y.id })));
			}
		}

		if (successfulUploads.length > 0) {
			onFileSelected([...files, ...successfulUploads]);
		}
	};

	const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const droppedFiles = Array.from(e.dataTransfer.files);
		handleFiles(droppedFiles);
	};

	const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const selectedFiles = Array.from(e.target.files);

			handleFiles(selectedFiles);
		}
		// Reset input value to allow selecting the same file again
		if (inputRef.current) {
			inputRef.current.value = '';
		}
	};

	const handleRemoveFile = async (index: number) => {
		const newFiles = files.filter((_, i) => i !== index);

		onDeleteFile?.(files[index]);

		onFileSelected(newFiles);
	};

	const handleClick = () => {
		if (files.length >= SURVEY_FILE_INPUT_MAX_FILES) {
			toast.error(`Maximum of ${SURVEY_FILE_INPUT_MAX_FILES} files already selected`);
			return;
		}
		inputRef.current?.click();
	};

	const isUploading = uploadMutation.isPending;

	return (
		<div className={css(styleSheet.container)}>
			{files.length > 0 && (
				<div className={css(styleSheet.fileList)}>
					{files.map((file, index) => (
						<div key={`${file.fileName}-${index}`} className={css(styleSheet.fileItem)}>
							<span className={css(styleSheet.fileName)}>{file.fileName}</span>
							<button
								type='button'
								className={css(styleSheet.removeButton)}
								onClick={e => {
									e.stopPropagation();
									handleRemoveFile(index);
								}}
								aria-label={`Remove ${file.fileName}`}
								disabled={isUploading}
							>
								<XIcon className={css(styleSheet.removeIcon)} />
							</button>
						</div>
					))}
				</div>
			)}

			{files.length < 5 && (
				<div
					className={css(
						styleSheet.dragContainer,
						isDragging && styleSheet.dragContainerActive,
						isUploading && styleSheet.dragContainerDisabled
					)}
					onDragEnter={isUploading ? undefined : handleDragEnter}
					onDragOver={isUploading ? undefined : handleDragOver}
					onDragLeave={isUploading ? undefined : handleDragLeave}
					onDrop={isUploading ? undefined : handleDrop}
					onClick={isUploading ? undefined : handleClick}
				>
					<input
						ref={inputRef}
						type='file'
						onChange={handleFileInputChange}
						multiple
						accept={acceptedFileTypes.join(',')}
						className={css(styleSheet.hidden)}
						disabled={isUploading}
						max={SURVEY_FILE_INPUT_MAX_FILES}
					/>
					{isUploading ? (
						<div className={css(styleSheet.loadingContainer)}>
							<div className={css(styleSheet.spinner)} />
							<div className={css(styleSheet.loadingText)}>Uploading files...</div>
						</div>
					) : (
						<div className={css(styleSheet.dragInfo)}>
							<div className={css(styleSheet.dragText)}>Drag and drop files to upload</div>

							<div className={css(styleSheet.dragFooter)}>Click to browse device</div>
						</div>
					)}
				</div>
			)}
			{error && <ErrorMessage message={error?.message} />}
		</div>
	);
};
